(() => {
  const ROOT_SELECTOR = '[data-k18-awards]';
  const TRANSITION = 'transform 480ms cubic-bezier(0.23, 1, 0.32, 1)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function initAwards(root) {
    if (!root || root._k18AwardsDestroy) return;

    const viewport = root.querySelector('[data-k18-awards-viewport]');
    const track = root.querySelector('[data-k18-awards-track]');
    const previousButton = root.querySelector('[data-k18-awards-prev]');
    const nextButton = root.querySelector('[data-k18-awards-next]');
    const pauseButton = root.querySelector('[data-k18-awards-pause]');
    const dots = Array.from(root.querySelectorAll('[data-k18-awards-dot]'));
    const originals = track ? Array.from(track.querySelectorAll(':scope > [data-k18-awards-slide]')) : [];

    if (!viewport || !track || originals.length < 2) {
      root._k18AwardsDestroy = () => {};
      return;
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const count = originals.length;
    let slides;
    let index = count;
    let timer;
    let observer;
    let isVisible = !('IntersectionObserver' in window);
    let isHovered = false;
    let hasFocus = false;
    let userPaused = false;
    let pointerStartX = null;
    let suppressClickUntil = 0;

    function makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.k18AwardsClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('data-shopify-editor-block-id');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    originals.forEach((slide) => before.appendChild(makeClone(slide)));
    originals.forEach((slide) => after.appendChild(makeClone(slide)));
    track.prepend(before);
    track.append(after);
    slides = Array.from(track.querySelectorAll(':scope > [data-k18-awards-slide]'));

    function selectedIndex() {
      return ((index - count) % count + count) % count;
    }

    function updateControls() {
      const selected = selectedIndex();
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === selected;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      root.dataset.k18AwardsPaused = String(userPaused);
      if (pauseButton) {
        pauseButton.setAttribute('aria-pressed', String(userPaused));
        pauseButton.setAttribute('aria-label', userPaused ? 'Start automatisk prisslider' : 'Pause automatisk prisslider');
      }
    }

    function render(animate) {
      const currentSlide = slides[index];
      if (!currentSlide) return;
      track.style.transition = animate && !mediaQuery.matches ? TRANSITION : 'none';
      track.style.transform = `translate3d(${-currentSlide.offsetLeft}px, 0, 0)`;
      updateControls();
    }

    function settleLoopPosition() {
      if (index >= count * 2) {
        index -= count;
        render(false);
      } else if (index < count) {
        index += count;
        render(false);
      }
    }

    function moveBy(delta) {
      index += delta;
      render(true);
      if (mediaQuery.matches) settleLoopPosition();
    }

    function moveTo(target) {
      index = count + target;
      render(true);
      if (mediaQuery.matches) settleLoopPosition();
    }

    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = undefined;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (userPaused || mediaQuery.matches || !isVisible || isHovered || hasFocus) return;
      timer = window.setInterval(() => moveBy(1), 5500);
    }

    function pauseAutoplay() {
      userPaused = true;
      updateControls();
      stopAutoplay();
    }

    function onPrevious() {
      pauseAutoplay();
      moveBy(-1);
    }

    function onNext() {
      pauseAutoplay();
      moveBy(1);
    }

    function onPauseToggle() {
      userPaused = !userPaused;
      updateControls();
      startAutoplay();
    }

    function onDotClick(event) {
      const dot = event.currentTarget;
      pauseAutoplay();
      moveTo(Number(dot.dataset.k18AwardsDot));
    }

    function onTransitionEnd(event) {
      if (event.target === track && event.propertyName === 'transform') {
        settleLoopPosition();
      }
    }

    function onPointerDown(event) {
      pointerStartX = event.clientX;
      pauseAutoplay();
    }

    function onPointerUp(event) {
      if (pointerStartX === null) return;
      const distance = event.clientX - pointerStartX;
      pointerStartX = null;
      if (Math.abs(distance) < 32) return;
      suppressClickUntil = Date.now() + 350;
      moveBy(distance < 0 ? 1 : -1);
    }

    function onPointerCancel() {
      pointerStartX = null;
    }

    function onViewportClick(event) {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    function onMouseEnter() {
      isHovered = true;
      stopAutoplay();
    }

    function onMouseLeave() {
      isHovered = false;
      startAutoplay();
    }

    function onFocusIn() {
      hasFocus = true;
      stopAutoplay();
    }

    function onFocusOut(event) {
      if (!root.contains(event.relatedTarget)) {
        hasFocus = false;
        startAutoplay();
      }
    }

    function onMotionChange() {
      if (mediaQuery.matches) {
        stopAutoplay();
        render(false);
      } else {
        startAutoplay();
      }
    }

    function onResize() {
      render(false);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        isVisible = entries[0].isIntersecting;
        startAutoplay();
      }, { threshold: 0.2 });
      observer.observe(root);
    }

    previousButton?.addEventListener('click', onPrevious);
    nextButton?.addEventListener('click', onNext);
    pauseButton?.addEventListener('click', onPauseToggle);
    dots.forEach((dot) => dot.addEventListener('click', onDotClick));
    track.addEventListener('transitionend', onTransitionEnd);
    viewport.addEventListener('pointerdown', onPointerDown, { passive: true });
    viewport.addEventListener('pointerup', onPointerUp, { passive: true });
    viewport.addEventListener('pointercancel', onPointerCancel, { passive: true });
    viewport.addEventListener('click', onViewportClick, true);
    root.addEventListener('mouseenter', onMouseEnter);
    root.addEventListener('mouseleave', onMouseLeave);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    mediaQuery.addEventListener('change', onMotionChange);
    window.addEventListener('resize', onResize, { passive: true });

    render(false);
    startAutoplay();

    root._k18AwardsDestroy = () => {
      stopAutoplay();
      observer?.disconnect();
      previousButton?.removeEventListener('click', onPrevious);
      nextButton?.removeEventListener('click', onNext);
      pauseButton?.removeEventListener('click', onPauseToggle);
      dots.forEach((dot) => dot.removeEventListener('click', onDotClick));
      track.removeEventListener('transitionend', onTransitionEnd);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerCancel);
      viewport.removeEventListener('click', onViewportClick, true);
      root.removeEventListener('mouseenter', onMouseEnter);
      root.removeEventListener('mouseleave', onMouseLeave);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      mediaQuery.removeEventListener('change', onMotionChange);
      window.removeEventListener('resize', onResize);
      track.querySelectorAll('[data-k18-awards-clone]').forEach((clone) => clone.remove());
      track.style.transition = '';
      track.style.transform = '';
      delete root._k18AwardsDestroy;
    };
  }

  function initWithin(container) {
    if (!container) return;
    if (container.matches?.(ROOT_SELECTOR)) initAwards(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initAwards);
  }

  function destroyWithin(container) {
    if (!container) return;
    const roots = [];
    if (container.matches?.(ROOT_SELECTOR)) roots.push(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => root._k18AwardsDestroy?.());
  }

  function bindThemeEvents() {
    if (window._k18AwardsThemeEventsBound) return;
    window._k18AwardsThemeEventsBound = true;
    document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
    document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  }

  function boot() {
    initWithin(document);
    bindThemeEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
